import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/api.js';
import { useLang } from '../lib/i18n.js';
import { PageHead, Avatar, Badge, EmptyState } from '../components/ui.js';

interface User {
  user_id: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'agent' | 'viewer';
  is_active: boolean;
  notification_prefs: Record<string, unknown>;
  created_at: string;
}

const ROLE_KIND: Record<User['role'], 'ok' | 'warn' | 'accent' | 'muted'> = {
  owner: 'accent', admin: 'ok', agent: 'warn', viewer: 'muted',
};

export function TeamPage() {
  const api = useApi();
  const { t, lang } = useLang();
  const { data, isLoading } = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get<{ items: User[] }>('/team/users'),
  });
  const rows = data?.items ?? [];
  const dateLocale = lang === 'ar' ? 'ar-EG' : 'en-US';

  return (
    <div className="wrap section-gap">
      <PageHead title={t('page.team.title')} sub={t('page.team.sub')} />

      {isLoading ? (
        <div className="muted" style={{ padding: 24 }}>{t('common.loading')}</div>
      ) : rows.length === 0 ? (
        <EmptyState icon="team" h={t('page.team.empty.h')} p={t('page.team.empty.p')} />
      ) : (
        <div className="card tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>{t('page.team.col.member')}</th>
                <th>{t('page.team.col.email')}</th>
                <th>{t('page.team.col.role')}</th>
                <th>{t('page.team.col.status')}</th>
                <th>{t('page.team.col.since')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.user_id}>
                  <td>
                    <div className="usercell">
                      <Avatar initial={(u.name?.[0] ?? u.email[0] ?? '?').toUpperCase()} size={36} grad={u.role === 'owner'} />
                      <div><div className="usercell__name">{u.name || '—'}</div></div>
                    </div>
                  </td>
                  <td className="muted" style={{ fontSize: 13 }}>{u.email}</td>
                  <td><Badge kind={ROLE_KIND[u.role]} plain>{t(`role.${u.role}`)}</Badge></td>
                  <td>
                    {u.is_active
                      ? <Badge kind="ok" plain icon="checkcircle">{t('page.team.active')}</Badge>
                      : <Badge kind="muted" plain>{t('page.team.inactive')}</Badge>}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {new Date(u.created_at).toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
